// ✅ PurchaseOrderListingComponent.jsx (FULL FILE — sidebar updated like DailyProgressMonthlyWise)

import React, { useEffect, useMemo, useState } from "react";
import {
  FaFileInvoiceDollar,
  FaCalendarAlt,
  FaBuilding,
  FaSearch,
  FaFilter,
  FaPlus,
  FaExclamationTriangle,
  FaCheckCircle,
  FaBars,
  FaHome,
  FaChartBar,
  FaUsers,
  FaFileAlt,
  FaTasks,
  FaWarehouse,
  FaSort,
} from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE = "http://nissi-india.com/api/purchaseOrderListing";

const toInt = (v, def = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : def;
};

const toNum = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, "").replace(/[₹\s]/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};

const fmtINR = (v) =>
  toNum(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const toDMY = (v) => {
  const s = String(v ?? "").trim();
  if (!s) return "";
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;

  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;

  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const toISO = (v) => {
  const s = String(v ?? "").trim();
  if (!s) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    const [dd, mm, yyyy] = s.split("/");
    return `${yyyy}-${mm}-${dd}`;
  }

  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const PurchaseOrderListingComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isDark, setIsDark] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMenuItem, setActiveMenuItem] = useState("Purchase Order");

  // ✅ filters
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [dateFilter, setDateFilter] = useState("");
  const [applyNonce, setApplyNonce] = useState(0);

  // ✅ data
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [purchaseOrdersRaw, setPurchaseOrdersRaw] = useState([]);

  // ✅ sorting
  const [sortKey, setSortKey] = useState("poNo");
  const [sortDir, setSortDir] = useState("asc");

  // ✅ paging
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // ✅ localStorage helper
  const getLS = (key) => localStorage.getItem(key) || "";

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype");
  const userIdLS = getLS("userId") || getLS("loginuserid");

  // ✅ numeric userId for payload
  const userId = useMemo(() => toInt(userIdLS || 0), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchIdLS,
      "x-branchtype": branchTypeLS,
      "x-userid": userIdLS,
    }),
    [finYearId, finYear, branchIdLS, branchTypeLS, userIdLS]
  );

  const contractId = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    const cid =
      qs.get("contractid") ||
      qs.get("contractId") ||
      sessionStorage.getItem("Selcontractid") ||
      sessionStorage.getItem("selcontractid") ||
      sessionStorage.getItem("contractId") ||
      "";

    const n = Number(cid || 0);
    return Number.isFinite(n) ? n : 0;
  }, [location.search]);

  const withContractId = (path) => {
    if (!contractId) return path;

    const url = new URL(path, window.location.origin);
    url.searchParams.set("contractId", contractId);
    return url.pathname + url.search;
  };

  const sidebarItems = useMemo(
    () => [
      { name: "Date/LOA", icon: FaCalendarAlt, path: withContractId("/OrderManagement/ProjectEntry") },
      { name: "BOQ/Operative", icon: FaFileInvoiceDollar, path: withContractId("/OrderManagement/BOQ") },
      { name: "Program/DPR", icon: FaChartBar, path: withContractId("/OrderManagement/DailyProgressMonthlyWise") },
      { name: "Purchase Order", icon: FaFileAlt, path: withContractId("/OrderManagement/PurchaseOrderListing") },
      { name: "Rate Approval", icon: FaCheckCircle, path: withContractId("/OrderManagement/RateApprove") },
      { name: "Risk/Issues", icon: FaExclamationTriangle, path: withContractId("/OrderManagement/RiskAndIssue") },
      { name: "Documentation", icon: FaFileAlt, path: withContractId("/OrderManagement/ProjectEntryDocumentation") },
      { name: "Photos", icon: FaBuilding, path: withContractId("/OrderManagement/PhotoDocumentation") },
      { name: "Reconciliation", icon: FaTasks, path: withContractId("/OrderManagement/Reconciliation") },
      { name: "Do/Don't's", icon: FaWarehouse, path: withContractId("/OrderManagement/Rules") },
      { name: "Vendor Details", icon: FaUsers, path: withContractId("/OrderManagement/ProjectVendorDetails") },
    ],
    [contractId]
  );

  const loadGrid = async () => {
    setLoading(true);
    setError("");

    try {
      if (!contractId || contractId <= 0) {
        setPurchaseOrdersRaw([]);
        setError("Contract ID missing in URL");
        return;
      }

      const res = await axios.get(`${API_BASE}/list`, {
        params: {
          contractId,
          userId,
        },
        headers: commonHeaders,
      });

      if (res.data?.success) {
        setPurchaseOrdersRaw(res.data?.rows || []);
        setPage(1);
      } else {
        setPurchaseOrdersRaw([]);
        setError(res.data?.message || "Failed to load");
      }
    } catch (e) {
      console.error("Purchase Order list load error:", e);
      setPurchaseOrdersRaw([]);
      setError("Server error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const moduleSelect =
      sessionStorage.getItem("ModuleSelect") ||
      sessionStorage.getItem("moduleSelect") ||
      "ERP";

    if (String(moduleSelect).toUpperCase() !== "ERP") {
      window.location.href = "/ModuleHomePage";
      return;
    }

    loadGrid();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, userId, commonHeaders]);

  const purchaseOrders = useMemo(() => {
    return (purchaseOrdersRaw || []).map((r) => {
      const poId = r?.fld_PoId ?? r?.fld_poid ?? r?.poId ?? 0;
      const poNo = r?.fld_POno ?? r?.fld_pono ?? r?.poNo ?? "";
      const poDate = r?.fld_podate ?? r?.poDate ?? "";
      const quotationNo = r?.fld_quotationno ?? r?.quotationNo ?? r?.quotationno ?? "";
      const grandTotal = r?.fld_grandtotal ?? r?.grandTotal ?? r?.grandtotal ?? "";

      const vendor = r?.vendor || r?.fld_vendorname || "-";
      const items = r?.items || r?.fld_items || "-";
      const status = r?.status || r?.fld_status || "N/A";

      return {
        _raw: r,
        poRowId: poId,
        id: poNo,
        vendor,
        date: toISO(poDate) || poDate,
        amount: grandTotal,
        status,
        items,
        quotationNo,
        poDateDMY: toDMY(poDate),
        poNo,
        grandTotalNum: toNum(grandTotal),
      };
    });
  }, [purchaseOrdersRaw]);

  const filtered = useMemo(() => {
    void applyNonce;

    const s = String(searchText || "").trim().toLowerCase();
    const st = String(statusFilter || "All Status");
    const df = String(dateFilter || "").trim();

    return (purchaseOrders || []).filter((po) => {
      const matchesSearch =
        !s ||
        String(po.poNo || "").toLowerCase().includes(s) ||
        String(po.vendor || "").toLowerCase().includes(s) ||
        String(po.items || "").toLowerCase().includes(s) ||
        String(po.quotationNo || "").toLowerCase().includes(s);

      const matchesStatus = st === "All Status" ? true : String(po.status) === st;
      const matchesDate = !df ? true : String(po.date).slice(0, 10) === df;

      return matchesSearch && matchesStatus && matchesDate;
    });
  }, [purchaseOrders, searchText, statusFilter, dateFilter, applyNonce]);

  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;

    const val = (po) => {
      switch (sortKey) {
        case "poNo":
          return String(po.poNo || "");
        case "vendor":
          return String(po.vendor || "");
        case "date":
          return String(po.date || "");
        case "amount":
          return Number(po.grandTotalNum || 0);
        default:
          return String(po.poNo || "");
      }
    };

    const arr = [...(filtered || [])];
    arr.sort((a, b) => {
      const av = val(a);
      const bv = val(b);

      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });

    return arr;
  }, [filtered, sortKey, sortDir]);

  const totalPages = useMemo(() => {
    const n = Math.ceil((sorted?.length || 0) / pageSize);
    return n <= 0 ? 1 : n;
  }, [sorted]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return (sorted || []).slice(start, start + pageSize);
  }, [sorted, page]);

  const totals = useMemo(() => {
    const totalCount = sorted?.length || 0;
    const totalValue = (sorted || []).reduce((sum, po) => sum + (po.grandTotalNum || 0), 0);

    const byStatus = (st) => (sorted || []).filter((x) => String(x.status) === st).length;

    return {
      totalCount,
      totalValue,
      pendingCount: byStatus("Pending"),
      deliveredCount: byStatus("Delivered"),
    };
  }, [sorted]);

  const handleMenuItemClick = (menuItem) => {
    setActiveMenuItem(menuItem.name);
    console.log(`Navigating to: ${menuItem.name}`);
  };

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((p) => (p === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const handleApplyFilter = () => {
    setApplyNonce((p) => p + 1);
    setPage(1);
  };

  const handleAddNew = () => {
    sessionStorage.setItem("HeadName", "PURCHASE ORDER");
    navigate(withContractId("/OrderManagement/PurchaseOrderDashboard"));
  };

  const renderContent = () => {
    switch (activeMenuItem) {
      case "Purchase Order":
        return (
          <>
            <div
              className={`rounded-2xl shadow-xl border ${
                isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"
              }`}
            >
              <div className="bg-blue-600 text-white px-6 py-4 rounded-t-2xl">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold">PURCHASE ORDER LISTING</h2>
                  <div className="flex gap-3">
                    <button
                      onClick={handleAddNew}
                      className="px-4 py-2 bg-blue-500 hover:bg-blue-400 text-white rounded-lg flex items-center gap-2 transition-all duration-200"
                    >
                      <FaPlus />
                      Add New PO
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-6 border-b border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="relative">
                    <FaSearch className={`absolute left-3 top-3 ${isDark ? "text-gray-400" : "text-gray-500"}`} />
                    <input
                      type="text"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      placeholder="Search Purchase Orders..."
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                  </div>

                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className={`px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option>All Status</option>
                    <option>Pending</option>
                    <option>Approved</option>
                    <option>Processing</option>
                    <option>Delivered</option>
                    <option>N/A</option>
                  </select>

                  <input
                    type="date"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className={`px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />

                  <button
                    onClick={handleApplyFilter}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-all duration-200"
                  >
                    <FaFilter />
                    Apply Filter
                  </button>
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                    Contract ID: <span className="font-semibold">{contractId || "-"}</span>
                    {branchIdLS ? (
                      <>
                        {" "}
                        • Branch ID: <span className="font-semibold">{branchIdLS}</span>
                      </>
                    ) : null}
                    {branchTypeLS ? (
                      <>
                        {" "}
                        • Branch Type: <span className="font-semibold">{branchTypeLS}</span>
                      </>
                    ) : null}
                    {userIdLS ? (
                      <>
                        {" "}
                        • User ID: <span className="font-semibold">{userIdLS}</span>
                      </>
                    ) : null}
                    {loading ? " • Loading..." : ""}
                    {error ? ` • ${error}` : ""}
                  </div>

                  <button
                    onClick={loadGrid}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isDark ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-800"
                    }`}
                  >
                    Refresh
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${isDark ? "bg-gray-700" : "bg-gray-50"}`}>
                    <tr>
                      <th
                        className={`px-6 py-4 text-left text-xs font-medium uppercase tracking-wider ${
                          isDark ? "text-gray-300" : "text-gray-500"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          PO Number
                          <FaSort className="cursor-pointer" onClick={() => toggleSort("poNo")} />
                        </div>
                      </th>

                      <th
                        className={`px-6 py-4 text-left text-xs font-medium uppercase tracking-wider ${
                          isDark ? "text-gray-300" : "text-gray-500"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          Date
                          <FaSort className="cursor-pointer" onClick={() => toggleSort("date")} />
                        </div>
                      </th>

                      <th
                        className={`px-6 py-4 text-left text-xs font-medium uppercase tracking-wider ${
                          isDark ? "text-gray-300" : "text-gray-500"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          Amount
                          <FaSort className="cursor-pointer" onClick={() => toggleSort("amount")} />
                        </div>
                      </th>
                    </tr>
                  </thead>

                  <tbody
                    className={`${isDark ? "bg-gray-800" : "bg-white"} divide-y ${
                      isDark ? "divide-gray-700" : "divide-gray-200"
                    }`}
                  >
                    {loading ? (
                      <tr>
                        <td
                          colSpan={7}
                          className={`px-6 py-6 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}
                        >
                          Loading...
                        </td>
                      </tr>
                    ) : pageRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          className={`px-6 py-6 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}
                        >
                          {error ? error : "No Record Found"}
                        </td>
                      </tr>
                    ) : (
                      pageRows.map((po) => (
                        <tr
                          key={`${po.poRowId}-${po.poNo}`}
                          className={`hover:${isDark ? "bg-gray-700" : "bg-gray-50"} transition-colors duration-200`}
                        >
                          <td
                            className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                              isDark ? "text-white" : "text-gray-900"
                            }`}
                            title={`Quotation: ${po.quotationNo || "-"}`}
                          >
                            {po.poNo || "-"}
                          </td>

                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                            {po.poDateDMY || "-"}
                          </td>

                          <td
                            className={`px-6 py-4 whitespace-nowrap text-sm font-semibold ${
                              isDark ? "text-green-400" : "text-green-600"
                            }`}
                          >
                            ₹{fmtINR(po.amount)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div
                className={`px-6 py-4 border-t ${
                  isDark ? "border-gray-700" : "border-gray-200"
                } flex justify-between items-center`}
              >
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Showing {sorted.length === 0 ? 0 : (page - 1) * pageSize + 1} to{" "}
                  {Math.min(page * pageSize, sorted.length)} of {sorted.length} results
                </div>

                <div className="flex gap-2">
                  <button
                    className={`px-3 py-1 border rounded ${
                      isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"
                    } hover:bg-gray-100 transition-colors duration-200 disabled:opacity-50`}
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>

                  <button className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors duration-200">
                    {page}
                  </button>

                  <button
                    className={`px-3 py-1 border rounded ${
                      isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"
                    } hover:bg-gray-100 transition-colors duration-200 disabled:opacity-50`}
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
              <div
                className={`p-6 rounded-2xl shadow-xl border transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                  isDark
                    ? "bg-gradient-to-br from-blue-900/50 to-blue-800/30 backdrop-blur-md border-blue-700 text-white"
                    : "bg-gradient-to-br from-blue-500 to-blue-600 text-white border-blue-300"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm font-medium">Total POs</p>
                    <p className="text-3xl font-bold">{totals.totalCount}</p>
                  </div>
                  <FaFileAlt className="text-4xl text-blue-200" />
                </div>
              </div>

              <div
                className={`p-6 rounded-2xl shadow-xl border transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                  isDark
                    ? "bg-gradient-to-br from-green-900/50 to-green-800/30 backdrop-blur-md border-green-700 text-white"
                    : "bg-gradient-to-br from-green-500 to-green-600 text-white border-green-300"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm font-medium">Total Value</p>
                    <p className="text-3xl font-bold">₹{fmtINR(totals.totalValue)}</p>
                  </div>
                  <FaFileInvoiceDollar className="text-4xl text-green-200" />
                </div>
              </div>

              <div
                className={`p-6 rounded-2xl shadow-xl border transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                  isDark
                    ? "bg-gradient-to-br from-orange-900/50 to-orange-800/30 backdrop-blur-md border-orange-700 text-white"
                    : "bg-gradient-to-br from-orange-500 to-orange-600 text-white border-orange-300"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-100 text-sm font-medium">Pending</p>
                    <p className="text-3xl font-bold">{totals.pendingCount}</p>
                  </div>
                  <FaExclamationTriangle className="text-4xl text-orange-200" />
                </div>
              </div>

              <div
                className={`p-6 rounded-2xl shadow-xl border transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                  isDark
                    ? "bg-gradient-to-br from-purple-900/50 to-purple-800/30 backdrop-blur-md border-purple-700 text-white"
                    : "bg-gradient-to-br from-purple-500 to-purple-600 text-white border-purple-300"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-sm font-medium">Delivered</p>
                    <p className="text-3xl font-bold">{totals.deliveredCount}</p>
                  </div>
                  <FaCheckCircle className="text-4xl text-purple-200" />
                </div>
              </div>
            </div>
          </>
        );

      default:
        return (
          <div
            className={`rounded-2xl shadow-xl border ${
              isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"
            }`}
          >
            <div className="bg-blue-600 text-white px-6 py-3 rounded-t-2xl">
              <h2 className="text-lg font-bold">{activeMenuItem.toUpperCase()} MANAGEMENT</h2>
            </div>
            <div className="p-6">
              <p className={`text-center py-8 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                {activeMenuItem} content will be displayed here.
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className={`min-h-screen transition-all duration-300 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
      <div
        className={`sticky top-0 z-50 backdrop-blur-md border-b ${
          isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        } shadow-lg`}
      >
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? "hover:bg-gray-700 text-gray-300" : "hover:bg-gray-100 text-gray-600"
                }`}
              >
                <FaBars size={20} />
              </button>
              <div className="flex items-center gap-3">
                <FaHome className="text-blue-600 text-xl" />
                <span className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Project Management System
                </span>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsDark(!isDark)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? "bg-yellow-600 hover:bg-yellow-700 text-white" : "bg-gray-800 hover:bg-gray-900 text-white"
                }`}
              >
                {isDark ? "☀️" : "🌙"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex">
        <div
          className={`${sidebarOpen ? "w-64" : "w-16"} transition-all duration-300 ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
          } border-r min-h-screen`}
        >
          <div className="p-4">
            {sidebarItems.map((item, index) => (
              <Link
                to={item.path}
                key={index}
                onClick={() => handleMenuItemClick(item)}
                className={`flex items-center gap-3 p-3 rounded-lg mb-2 cursor-pointer transition-all duration-200 ${
                  location.pathname === new URL(item.path, window.location.origin).pathname
                    ? isDark
                      ? "bg-blue-600 text-white"
                      : "bg-blue-500 text-white"
                    : isDark
                    ? "hover:bg-gray-700 text-gray-300"
                    : "hover:bg-gray-100 text-gray-700"
                }`}
              >
                <item.icon size={18} />
                {sidebarOpen && <span className="text-sm font-medium">{item.name}</span>}
              </Link>
            ))}
          </div>
        </div>

        <div className="flex-1">
          <div
            className={`transition-all duration-300 min-h-screen ${
              isDark
                ? "bg-gradient-to-br from-gray-900 via-gray-800 to-blue-900"
                : "bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"
            }`}
          >
            <div className="p-6">{renderContent()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderListingComponent;